// ...existing code...
import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { appAbsPath, applyGlobalHeaderMargin, deleteCookie, executeLaravelAPI, userToken } from './Utils';
import { SearchVisibilityProvider } from '../Front/Layouts/SearchVisibilityContext';
import { setUserId } from './UserActivatyTracker';
import { Helmet } from 'react-helmet-async';

// Admin Layout
import Header from './Layouts/Header';
import Sidebar from './Layouts/Sidebar';
import Footer from './Layouts/Footer';

// Admin Components
import AdminLogin from './Auth/AdminLogin';
import Dashboard from './Pages/Dashboard';
import ProjectsList from './Pages/Project/ProjectsList';
import VerifiedProjectList from './Pages/Project/VerifiedProjectList';
import UpdatedProjectList from './Pages/Project/UpdatedProjectList';
import ProjectForm from './Pages/ProjectForm';
import Leads from './Pages/Project/Leads';
import DeveloperAddNewForm from './Pages/Developer/DeveloperAddNew';
import DeveloperEditForm from './Pages/Developer/DeveloperEdit';
import OfferAddNewForm from './Pages/Offer/OfferAddNew';
import CouponList from './Pages/Offer/CouponList';
import LocationAddNewForm from './Pages/Location/LocationAddNew';
import LocationEditForm from './Pages/Location/LocationEdit';
import DisabledProjectList from './Pages/Project/DisabledProjectList';
import LocationsList from './Pages/Location/LocationsList';
import DevelopersList from './Pages/Developer/DevelopersList';
import ShortUpdatesList from './Pages/ShortUpdates/ShortUpdatesList';
import ShortUpdateAddNew from './Pages/ShortUpdates/ShortUpdateAddNew';
import ShortUpdateEdit from './Pages/ShortUpdates/ShortUdateEdit';
import OffersList from './Pages/Offer/OffersList';
import OfferEditForm from './Pages/Offer/OfferEdit';
import UsersList from './Auth/UsersList';
import UserEditForm from './Auth/UserEdit';
import DeveloperDetails from './Pages/Developerdetails/DeveloperDetails';
import LocalityConfigurationTable from './Pages/LocalitiesConfiguration/LocalitiesConfiguration';
import UploadExcelFile from './Pages/upload-excel/UploadExcelFile';
import ProjectMediaReport from './Pages/Project/ProjectMediaReport';
import ProjectPriceReport from './Pages/Project/ProjectPriceReport';
import SeoMetadataPage from './Pages/Seo/SeoMetadataPage';

import '../../App.scss';
import { set } from 'mongoose';
// ...existing code...

const AdminApp = () => {
    const location = useLocation();
    const loginPath = `${appAbsPath}/portaladmin/login`;
    const isLoginRoute = location.pathname === loginPath;
    const isAuthenticated = typeof window !== 'undefined' && !!localStorage.getItem('bwToken');

    // Normalize excessive slashes in URL path and redirect to the cleaned URL
    useEffect(() => {
        if (typeof window !== 'undefined') {
            try {
                const checkAdminAuthentication = async () => {
                    const userdata = await executeLaravelAPI('check-auth', '', 'GET', userToken);  
                    if (!userdata?.authenticated && userToken) {
                        alert("Your session has been expired. Redirecting to login page...");
                        await executeLaravelAPI('logout', '', 'POST', userToken);
                        deleteCookie('userData');
                        localStorage.removeItem("bwToken");
                        window.location.href = loginPath;
                    } else {
                        console.log("Admin authentication check successful");
                    }
                }
                const currentUrl = new URL(window.location.href);
                const originalPath = currentUrl.pathname;
                const normalizedPath = originalPath.replace(/\/{2,}/g, '/');

                if (normalizedPath !== originalPath) {
                    currentUrl.pathname = normalizedPath;
                    window.location.replace(currentUrl.toString());
                }
                checkAdminAuthentication();
                // Then every 1 minute
                const interval = setInterval(() => {
                    checkAdminAuthentication();
                }, 60000);
                return () => clearInterval(interval); // cleanup on unmount
            } catch (e) {
                // If URL parsing fails, do nothing
            }
        }
    }, []);

    // Check if we're on staging domain
    const isStaging = typeof window !== 'undefined' &&
        (window.location.hostname === 'stagingportal.beyondwalls.com' ||
            window.location.hostname.includes('staging'));

    // Disable browser scroll restoration for admin panel
    useEffect(() => {
        if (typeof window !== 'undefined' && 'history' in window && 'scrollRestoration' in window.history) {
            window.history.scrollRestoration = 'manual';
        }
    }, []);

    // Scroll to top when route changes in admin panel
    useEffect(() => {
        const scrollToTop = () => {
            window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
            window.scrollTo(0, 0);
            if (document.documentElement) {
                document.documentElement.scrollTop = 0;
            }
            if (document.body) {
                document.body.scrollTop = 0;
            }
            const contentWrapper = document.querySelector('.content-wrapper');
            if (contentWrapper) {
                contentWrapper.scrollTop = 0;
                contentWrapper.scrollTo({ top: 0, left: 0, behavior: 'auto' });
            }
            const mainPanel = document.querySelector('.main-panel');
            if (mainPanel) {
                mainPanel.scrollTop = 0;
                mainPanel.scrollTo({ top: 0, left: 0, behavior: 'auto' });
            }
            const pageBodyWrapper = document.querySelector('.page-body-wrapper');
            if (pageBodyWrapper) {
                pageBodyWrapper.scrollTop = 0;
                pageBodyWrapper.scrollTo({ top: 0, left: 0, behavior: 'auto' });
            }
            const containerScroller = document.querySelector('.container-scroller');
            if (containerScroller) {
                containerScroller.scrollTop = 0;
                containerScroller.scrollTo({ top: 0, left: 0, behavior: 'auto' });
            }
        };

        scrollToTop();
        requestAnimationFrame(scrollToTop);
        setTimeout(scrollToTop, 0);
        setTimeout(scrollToTop, 10);
        setTimeout(scrollToTop, 50);
        setTimeout(scrollToTop, 100);
        setTimeout(scrollToTop, 200);
    }, [location.pathname]);

    useEffect(() => {
        setUserId();
    }, []);

    useEffect(() => {
        const handleMarginAdjustment = () => {
            applyGlobalHeaderMargin(".header", "dynamicHeaderMargin");
        };
        if (document.readyState === "complete") {
            handleMarginAdjustment();
        } else {
            window.addEventListener("load", handleMarginAdjustment);
        }
        window.addEventListener("resize", handleMarginAdjustment);

        return () => {
            window.removeEventListener("load", handleMarginAdjustment);
            window.removeEventListener("resize", handleMarginAdjustment);
        };
    }, []);

    const pageHelmet = (
        <Helmet>
            <title>Admin Panel</title>
            {isStaging && <meta name="robots" content="noindex, nofollow" />}
        </Helmet>
    );

    if (isLoginRoute) {
        // If already logged in, never show login page
        if (isAuthenticated) {
            return <Navigate to={`${appAbsPath}/portaladmin/dashboard`} replace />;
        }
        return (
            <div>
                {pageHelmet}
                <ToastContainer />
                <AdminLogin />
            </div>
        );
    }

    return (
        <div>
            {pageHelmet}
            <ToastContainer />

            <div className="container-scroller">
                <SearchVisibilityProvider>
                    <Header loggeduserdetails={{}} />
                </SearchVisibilityProvider>
                <div className="container-fluid page-body-wrapper">
                    <Sidebar />
                    <div className="main-panel">
                        <div className="content-wrapper">
                            {!isAuthenticated && (
                                <div style={{ padding: '16px 0' }}>
                                    <Navigate to={loginPath} replace />
                                </div>
                            )}
                            <Routes>
                                {/* Admin build default landing */}
                                <Route
                                    exact
                                    path={`${appAbsPath}/`}
                                    element={
                                        <Navigate
                                            to={isAuthenticated ? `${appAbsPath}/portaladmin/dashboard` : loginPath}
                                            replace
                                        />
                                    }
                                />
                                <Route
                                    exact
                                    path={`${appAbsPath}/portaladmin`}
                                    element={
                                        <Navigate
                                            to={isAuthenticated ? `${appAbsPath}/portaladmin/dashboard` : loginPath}
                                            replace
                                        />
                                    }
                                />

                                {/* Login route (admin build) */}
                                <Route
                                    exact
                                    path={loginPath}
                                    element={
                                        isAuthenticated
                                            ? <Navigate to={`${appAbsPath}/portaladmin/dashboard`} replace />
                                            : <AdminLogin />
                                    }
                                />

                                {/* Protected admin routes */}
                                <Route exact path={`${appAbsPath}/portaladmin/dashboard`} element={<Dashboard />} />
                                <Route exact path={`${appAbsPath}/portaladmin/projects`} element={<ProjectsList />} />
                                <Route exact path={`${appAbsPath}/portaladmin/add/project`} element={<ProjectForm />} />
                                <Route exact path={`${appAbsPath}/portaladmin/add/developer`} element={<DeveloperAddNewForm />} />
                                <Route exact path={`${appAbsPath}/portaladmin/developers`} element={<DevelopersList />} />
                                <Route exact path={`${appAbsPath}/portaladmin/developer/edit/:id`} element={<DeveloperEditForm />} />
                                <Route exact path={`${appAbsPath}/portaladmin/add/offer`} element={<OfferAddNewForm />} />
                                <Route exact path={`${appAbsPath}/portaladmin/couponlist`} element={<CouponList />} />
                                <Route exact path={`${appAbsPath}/portaladmin/add/location`} element={<LocationAddNewForm />} />
                                <Route exact path={`${appAbsPath}/portaladmin/locations`} element={<LocationsList />} />
                                <Route exact path={`${appAbsPath}/portaladmin/offers`} element={<OffersList />} />
                                <Route exact path={`${appAbsPath}/portaladmin/users`} element={<UsersList />} />
                                <Route exact path={`${appAbsPath}/portaladmin/user/edit/:id`} element={<UserEditForm />} />
                                <Route exact path={`${appAbsPath}/portaladmin/offer/edit/:id`} element={<OfferEditForm />} />
                                <Route exact path={`${appAbsPath}/portaladmin/developer-projects`} element={<DeveloperDetails />} />
                                <Route path={`${appAbsPath}/portaladmin/locality-configurations`} element={<LocalityConfigurationTable />} />
                                <Route exact path={`${appAbsPath}/portaladmin/project-media-report`} element={<ProjectMediaReport />} />
                                <Route exact path={`${appAbsPath}/portaladmin/project-price-report`} element={<ProjectPriceReport />} />
                                <Route exact path={`${appAbsPath}/portaladmin/location/edit/:id`} element={<LocationEditForm />} />
                                <Route exact path={`${appAbsPath}/portaladmin/seo-metadata`} element={<SeoMetadataPage />} />
                                <Route exact path={`${appAbsPath}/portaladmin/verifiedprojects`} element={<VerifiedProjectList />} />
                                <Route exact path={`${appAbsPath}/portaladmin/updatedprojects`} element={<UpdatedProjectList />} />
                                <Route exact path={`${appAbsPath}/portaladmin/disabledprojects`} element={<DisabledProjectList />} />
                                <Route exact path={`${appAbsPath}/portaladmin/leads`} element={<Leads />} />

                                <Route exact path={`${appAbsPath}/portaladmin/shortupdates`} element={<ShortUpdatesList />} />
                                <Route exact path={`${appAbsPath}/portaladmin/add-short-update`} element={<ShortUpdateAddNew />} />
                                <Route exact path={`${appAbsPath}/portaladmin/shortupdates/edit/:id`} element={<ShortUpdateEdit />} />
                                <Route exact path={`${appAbsPath}/portaladmin/upload-excel`} element={<UploadExcelFile />} />
                                <Route
                                    path="*"
                                    element={
                                        <Navigate
                                            to={isAuthenticated ? `${appAbsPath}/portaladmin/dashboard` : loginPath}
                                            replace
                                        />
                                    }
                                />
                            </Routes>
                        </div>
                        <Footer />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminApp;